import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PlatformMark } from './PlatformMark';
import type { PlatformId } from './PlatformIcon';
import { PLATFORM_LIST } from './platform-list';
import { Icons } from '@/shared/constants/icons';
import { formatDateTime } from '@/shared/lib/intl';
import { platformName } from '@/shared/lib/post-result-error';

interface ReceiptRowProps {
  /** As the API names it: INSTAGRAM, GOOGLE_BUSINESS. */
  platform: string;
  state: 'published' | 'failed' | 'pending';
  /** When it went out, for a published channel. */
  publishedAt?: string | null;
  /** The live post, when the channel has a public link. */
  url?: string | null;
  /** "Verified on Instagram · 10:42", when the check ran. */
  verified?: string | null;
  /** The channel no longer has the post. */
  gone?: boolean;
}

const isPlatformId = (id: string): id is PlatformId => PLATFORM_LIST.some((p) => p.id === id);

/**
 * One channel's publish outcome on a receipt (brief §1.6): its mark and name,
 * the time, a status (a `--good` check, a `--bad` cross, a `--warn` clock)
 * and an "Open post" link to the live post.
 */
export function ReceiptRow({ platform, state, publishedAt, url, verified, gone = false }: ReceiptRowProps): ReactElement {
  const { t } = useTranslation();
  const id = platform.toLowerCase();
  const tone = gone || state === 'failed' ? 'var(--bad)' : state === 'published' ? 'var(--good)' : 'var(--warn)';
  const Icon = gone || state === 'failed' ? Icons.close : state === 'published' ? Icons.check : Icons.clock;
  const statusLabel = gone ? t('receipt.goneShort') : t(`receipt.status.${state}`);
  return (
    <li className="flex items-center gap-3 py-2.5 border-t border-[var(--line)] first:border-t-0" data-testid="receipt-row" data-state={gone ? 'gone' : state}>
      {isPlatformId(id) && <PlatformMark platform={id} size={24} className="shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-body font-medium text-[var(--ink)]">
          {platformName(platform)}
          <span className="inline-flex items-center gap-1 text-meta font-semibold" style={{ color: tone }}>
            <Icon size={13} aria-hidden="true" />
            {statusLabel}
          </span>
        </div>
        {state === 'published' && publishedAt && <div className="text-meta text-[var(--ink-3)]">{formatDateTime(publishedAt)}</div>}
        {verified && !gone && <div className="text-meta text-[var(--ink-3)]">{verified}</div>}
        {gone && <div className="text-meta text-[var(--bad)]">{t('receipt.gone', { platform: platformName(platform) })}</div>}
      </div>
      {state === 'published' && url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost btn-sm shrink-0 max-sm:min-h-11 inline-flex"
          aria-label={t('receipt.openPostOn', { platform: platformName(platform) })}
        >
          {t('receipt.openPost')}
          <Icons.externalLink size={13} aria-hidden="true" />
        </a>
      )}
    </li>
  );
}
